/**
 * TaskDescriptionEditor
 *
 * Inline edit-in-place for a task's description (`taskDetails`) + file/image attachments. Editable
 * by ANY user (unlike comments, which are author-only) — it mirrors how the description is edited
 * in the full-page task form. Saves via the shared UPDATE_TASK mutation and refetches to sync.
 *
 * IO Request tasks have no plain description — their content lives in the Campaign Setup Details
 * (see TaskRequestTypeDetailsEditable) — so this editor is only mounted for non-IO tasks.
 */

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Button, message } from 'antd';
import { handleUploadFilesToS3, getAddedMentionedUserIds } from '../utils/task.utils';
import { useTaskContext } from '../../../../core/components/tasks/useTaskContext';
import TaskInlineAttachments from './TaskInlineAttachments';
import MentionTextArea from './shared/MentionTextArea';

const TaskDescriptionEditor = ({ task, updateTask, refetchTask, onClose }) => {
  const { mentionUsers } = useTaskContext();
  const [value, setValue] = useState((task && task.taskDetails) || '');
  const [files, setFiles] = useState([]);
  const [images, setImages] = useState([]);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!task || !task.id) return;
    setSaving(true);
    try {
      const data = { taskDetails: value };
      const uploadedFiles = files.length ? await handleUploadFilesToS3(files) : [];
      const uploadedImages = images.length ? await handleUploadFilesToS3(images) : [];
      if (uploadedFiles.length) data.attachedFiles = { create: uploadedFiles };
      if (uploadedImages.length) data.attachedImages = { create: uploadedImages };
      // Ping only newly-added @mentions in the description.
      const mentions = getAddedMentionedUserIds(task.taskDetails, value, mentionUsers);
      await updateTask({ variables: { where: { id: task.id }, data, mentions } });
      if (refetchTask) refetchTask();
      message.success('Description updated.');
      onClose();
    } catch (e) {
      message.error('Failed to update description.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="task-description-inline__editor">
      <MentionTextArea
        value={value}
        onChange={setValue}
        autoSize={{ minRows: 5, maxRows: 16 }}
        placeholder="Add a description…"
        className="custom-textarea"
      />
      <TaskInlineAttachments files={files} setFiles={setFiles} images={images} setImages={setImages} />
      <div className="task-description-inline__editor-actions">
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button type="primary" onClick={handleSave} loading={saving}>
          Save
        </Button>
      </div>
    </div>
  );
};

TaskDescriptionEditor.propTypes = {
  task: PropTypes.shape({
    id: PropTypes.string,
    taskDetails: PropTypes.string,
  }),
  updateTask: PropTypes.func.isRequired,
  refetchTask: PropTypes.func,
  onClose: PropTypes.func.isRequired,
};

TaskDescriptionEditor.defaultProps = {
  task: null,
  refetchTask: undefined,
};

export default TaskDescriptionEditor;
